import { chatStreamingService } from "@services/chatStreamingApi";
import type { AraUserProfile } from "@services/user";
import React, { createContext, useState, useEffect } from "react";
import { useUserProfile } from "../../hooks/useUserProfile";

export interface UserContextType {
  user: AraUserProfile | undefined;
  isSubscriber: boolean;
  isLoading: boolean;
  locationPermission: "granted" | "denied" | "pending" | "not-asked" | "prompt";
  setLocationPermission: (
    permission: "granted" | "denied" | "pending" | "not-asked" | "prompt"
  ) => void;
}

export const UserContext = createContext<UserContextType | undefined>(
  undefined
);

const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { data: user, isLoading } = useUserProfile();
  const [locationPermission, setLocationPermission] = useState<
    "granted" | "denied" | "pending" | "not-asked" | "prompt"
  >("not-asked");

  // Calculamos si es suscriptor basándonos en el usuario actual
  const isSubscriber = Boolean(user?.subscriber === true);

  // Cargar el estado de permisos desde localStorage y verificar con el navegador
  useEffect(() => {
    const savedPermission = localStorage.getItem("locationPermission") as
      | "granted"
      | "denied"
      | "pending"
      | "not-asked"
      | "prompt";

    // Always check the real browser permission state first
    chatStreamingService
      .checkGeolocationPermission()
      .then((browserState) => {
        if (browserState === "denied") {
          // Browser has denied permission, update our state
          setLocationPermission("denied");
          localStorage.setItem("locationPermission", "denied");
        } else if (browserState === "granted") {
          // Browser has granted permission
          setLocationPermission("granted");
          localStorage.setItem("locationPermission", "granted");
        } else if (browserState === "prompt") {
          // Browser is prompting, but we can still try to get location
          // Set to granted so we can attempt to get coordinates
          setLocationPermission("granted");
          localStorage.setItem("locationPermission", "granted");
        } else if (savedPermission && savedPermission !== "not-asked") {
          // Use saved permission if browser state is 'unknown'
          setLocationPermission(savedPermission);
        } else {
          // First time or no saved state
          setLocationPermission("not-asked");
        }
      })
      .catch((error) => {
        console.warn("Error checking browser permission:", error);
        // Fallback to saved permission or not-asked
        if (savedPermission) {
          setLocationPermission(savedPermission);
        } else {
          setLocationPermission("not-asked");
        }
      });
  }, []);

  // Guardar el estado de permisos en localStorage
  const handleSetLocationPermission = (
    permission: "granted" | "denied" | "pending" | "not-asked" | "prompt"
  ) => {
    setLocationPermission(permission);
    localStorage.setItem("locationPermission", permission);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        isSubscriber,
        isLoading,
        locationPermission,
        setLocationPermission: handleSetLocationPermission
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

/**
 * Hook to access user context
 * @throws Error if used outside UserProvider
 */
export const useUser = (): UserContextType => {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export default UserProvider;
